import React, { FunctionComponent, useMemo, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  Alert,
  Dropdown,
  DropdownChangeObject,
  DropdownOption,
} from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../helpers/intlHooks";
import states from "../helpers/states";
import { HashLink } from "react-router-hash-link";
import routes from "../app/routes";
import {
  Ga4Event,
  Ga4EventType,
  MctPlanTypeStrings,
} from "../app/contexts/Analytics/types";
import { AnalyticsActionType } from "../app/contexts/Analytics/types";
import { useAppContext } from "../helpers/context-hooks/useAppContext";
import { usePlanStates } from "../helpers/query-hooks/usePlanStates";
import classNames from "classnames";

interface StateSelectorProps extends RouteComponentProps {
  buttonText: string;
  className?: string;
  /** Keeps the state dropdown from exceeding the DS maximum form control width */
  constrainDropdownWidth?: boolean;
  defaultLabel: string;
  defaultValue?: string;
  label?: string;
  programType: "PACE" | "SPAP";
}

export const STATE_SELECTOR_TEST_ID = "e2e-state-selector";

/**
 * StateSelector is used to display states for PACE and SPAP.
 */
const StateSelector: FunctionComponent<StateSelectorProps> = ({
  buttonText,
  className,
  constrainDropdownWidth = false,
  defaultLabel,
  defaultValue = "",
  label,
  location,
  programType,
}) => {
  // * Translate
  const t = useTranslate();

  // * Context
  const { state, dispatch } = useAppContext();

  // * State
  const [selectedState, setSelectedState] = useState(defaultValue);

  // * Queries
  const { paceStates, spapStates, isError } = usePlanStates(programType);

  // * Constants
  const route = programType == "PACE" ? routes.pace.plans : routes.spap.plans;
  const stateAbbreviations = programType === "PACE" ? paceStates : spapStates;

  // * Memos
  const stateOptions: DropdownOption[] = useMemo(() => {
    return [
      { label: defaultLabel, value: "" },
      ...states
        .filter(state => {
          return stateAbbreviations.includes(state.abbreviation);
        })
        .map(state => {
          return {
            label: state.name,
            value: state.abbreviation,
          };
        }),
    ];
  }, [defaultLabel, stateAbbreviations]);

  return (
    <>
      {isError ? (
        <Alert variation="error" role="alert">
          {t("state_selector.error")}
        </Alert>
      ) : (
        <div
          className={classNames("mct-c-dropdown-with-button", className, {
            "mct-c-dropdown-with-button--constrain-dropdown-width":
              constrainDropdownWidth,
          })}
        >
          <Dropdown
            className="mct-c-dropdown-with-button__dropdown-container"
            fieldClassName="mct-c-dropdown-with-button__dropdown"
            label={label || t("state")}
            labelClassName="ds-u-margin-top--0 mct-c-dropdown-with-button__label"
            name="state"
            options={stateOptions}
            onChange={(change: DropdownChangeObject) => {
              setSelectedState(change.currentTarget.value);
            }}
            value={selectedState}
            data-testid={STATE_SELECTOR_TEST_ID}
          />
          <div className="mct-c-dropdown-with-button__button-container">
            <HashLink
              className={`mct-c-dropdown-with-button__button ds-c-button ds-c-button--solid ds-c-button--big${
                selectedState ? "" : " ds-c-button--disabled"
              }`}
              to={`${route.replace(":state", selectedState)}${location.search}`}
              onClick={() => {
                dispatch({
                  type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                  settings: {
                    event_action:
                      programType === "PACE" ? "Find Plans" : "See Programs",
                    event_label: selectedState,
                    other_props: { event_category: programType },
                  },
                });
                if (programType === "PACE") {
                  dispatch({
                    type: AnalyticsActionType.SEND_GA4_EVENT,
                    settings: {
                      event_name: Ga4Event.PACE_PLAN_SEARCH,
                      event_type: Ga4EventType.UI_INTERACTION,
                      mct_coverage_wizard: !!state.cwCoverage,
                      mct_medigap: false,
                      mct_plan_type: MctPlanTypeStrings.PACE_PLAN,
                      text: "find plans",
                    },
                  });
                }
              }}
              data-testid="e2e-view-program"
            >
              {buttonText}
            </HashLink>
          </div>
        </div>
      )}
    </>
  );
};

export default withRouter(StateSelector);
